import React, { useMemo, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, FileSpreadsheet, FileText, FolderOpen, Image as ImageIcon, Plus, RefreshCw, LineChart } from 'lucide-react';
import { GainsMonitorPage as GainsMonitor } from '../components/GainsMonitorPage';
import { GainsPageHeader } from '../components/gains/GainsPageHeader';
import { ModuleLayout } from '../components/layout/ModuleLayout';
import type { GainsMonitorView } from '../components/GainsMonitorDashboard';
import type { RibbonConfig } from '../types/ribbon';
import { useProjectContext } from '../context/ProjectContext';
import { useOrganizationId } from '../contexts/WorkspaceContext';
import GainsMonitorService from '../services/gainsMonitorService';
import GainsImportService from '../services/gainsImportService';
import GainsExportService from '../services/gainsExportService';
import { ProjectService } from '../services/projectService';

export const GanhosPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const { selectedProject } = useProjectContext();
  const organizationId = useOrganizationId();
  const [view, setView] = useState<GainsMonitorView>('overview');
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedProjectId, setPickedProjectId] = useState('');
  const [formProjectId, setFormProjectId] = useState<string | null>(null);
  const [showKaizenForm, setShowKaizenForm] = useState(false);
  const [selectedKaizen, setSelectedKaizen] = useState<{ id: string; projectId: string | null; title: string } | null>(null);
  const [measurementRequest, setMeasurementRequest] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const projectIdParam = searchParams.get('projectId') || selectedProject?.id;

  const projectsQuery = useQuery({
    queryKey: ['enterprise-projects-list', organizationId],
    queryFn: () => ProjectService.list(organizationId),
    enabled: Boolean(organizationId) && pickerOpen,
    staleTime: 60_000,
  });

  const contextualProjectId = selectedKaizen?.projectId || projectIdParam || null;

  const handleRefresh = async () => {
    setRefreshing(true);
    setRefreshKey((current) => current + 1);
    await queryClient.invalidateQueries({ queryKey: ['ganhos'] });
    await queryClient.invalidateQueries({ queryKey: ['kaizen-result-validations'] });
    await queryClient.invalidateQueries({ queryKey: ['kaizen-result-snapshots'] });
    setRefreshing(false);
  };

  const handleNewKaizen = () => {
    if (projectIdParam) {
      setFormProjectId(projectIdParam);
      setShowKaizenForm(true);
      return;
    }
    setPickedProjectId('');
    setPickerOpen(true);
  };

  const confirmProject = () => {
    if (!pickedProjectId) return;
    setFormProjectId(pickedProjectId);
    setPickerOpen(false);
    setShowKaizenForm(true);
  };

  const loadExportData = () => Promise.all([
    GainsMonitorService.getKaizens({ organization_id: organizationId }),
    GainsMonitorService.getDashboardData(organizationId),
  ]);

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const [kaizens, dashboardData] = await loadExportData();
      await GainsExportService.exportGainsToExcel(kaizens, dashboardData);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Erro ao exportar Excel.');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const [kaizens, dashboardData] = await loadExportData();
      await GainsExportService.exportGainsToPDF(kaizens, dashboardData);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Erro ao exportar PDF.');
    } finally {
      setExporting(false);
    }
  };

  const handleExportImage = async () => {
    const target = document.querySelector('[data-gains-dashboard]');
    if (!(target instanceof HTMLElement)) {
      alert('A visão do monitor ainda não está disponível para captura.');
      return;
    }
    setExporting(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(target, { backgroundColor: '#ffffff', scale: 2, logging: false });
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve));
      if (!blob) throw new Error('Não foi possível gerar a imagem.');
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `GainsMonitor_Dashboard_${new Date().toISOString().slice(0, 10)}.png`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Erro ao exportar imagem.');
    } finally {
      setExporting(false);
    }
  };

  const handleImportExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const result = await GainsImportService.importKaizensFromExcel(file, organizationId);
      if (result.success) {
        alert(`Importação concluída. ${result.importedCount} de ${result.totalRows} Kaizens importados.`);
        await handleRefresh();
      } else {
        alert(`Falha na importação.\n${result.errors.join('\n')}`);
      }
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Erro ao importar arquivo.');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleKaizenSuccess = () => {
    setShowKaizenForm(false);
    setFormProjectId(null);
    void handleRefresh();
  };

  const ribbonConfig = useMemo<RibbonConfig>(() => ({
    moduleId: 'gains',
    groups: [
      {
        id: 'kaizen',
        label: 'Kaizen',
        position: 'left',
        actions: [
          { id: 'new-kaizen', label: 'Novo Kaizen', icon: Plus, onClick: handleNewKaizen, tooltip: 'Novo Kaizen', isPrimary: true },
          {
            id: 'project',
            label: 'Projeto',
            icon: FolderOpen,
            onClick: () => {
              if (contextualProjectId) navigate(`/projetos/${contextualProjectId}`);
            },
            hidden: !contextualProjectId,
            tooltip: 'Abrir o projeto deste Kaizen',
          },
        ],
      },
      {
        id: 'dados',
        label: 'Dados',
        position: 'left',
        actions: [
          {
            id: 'add-measurement',
            label: 'Medição',
            icon: LineChart,
            onClick: () => setMeasurementRequest((current) => current + 1),
            disabled: !selectedKaizen,
            tooltip: selectedKaizen ? 'Adicionar medição' : 'Selecione um Kaizen para adicionar uma medição',
          },
          {
            id: 'import',
            label: 'Importar',
            icon: FileSpreadsheet,
            onClick: () => fileInputRef.current?.click(),
            disabled: importing,
            tooltip: 'Importar',
          },
        ],
      },
      {
        id: 'saida',
        label: 'Saída',
        position: 'left',
        actions: [
          {
            id: 'export',
            label: 'Exportar',
            icon: Download,
            onClick: () => {},
            disabled: exporting,
            tooltip: 'Exportar',
            dropdown: [
              { id: 'excel', label: 'Excel', icon: FileSpreadsheet, onClick: () => { void handleExportExcel(); } },
              { id: 'pdf', label: 'PDF', icon: FileText, onClick: () => { void handleExportPdf(); } },
              { id: 'image', label: 'Imagem', icon: ImageIcon, onClick: () => { void handleExportImage(); } },
            ],
          },
        ],
      },
      {
        id: 'atualizacao',
        label: 'Atualização',
        position: 'left',
        actions: [
          {
            id: 'refresh',
            label: 'Atualizar',
            icon: RefreshCw,
            onClick: () => { void handleRefresh(); },
            disabled: refreshing,
            tooltip: 'Atualizar',
          },
        ],
      },
    ],
  }), [contextualProjectId, exporting, handleExportExcel, handleExportImage, handleExportPdf, handleNewKaizen, handleRefresh, importing, navigate, refreshing, selectedKaizen]);

  return (
    <ModuleLayout ribbonConfig={ribbonConfig}>
    <div className="flex h-full w-full flex-col overflow-hidden pb-8">
      <GainsPageHeader view={view} onViewChange={setView} />
      <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleImportExcel} className="hidden" />
      <div className="min-h-0 flex-1">
        <GainsMonitor
          organizationId={organizationId}
          projectId={projectIdParam}
          view={view}
          refreshKey={refreshKey}
          showKaizenForm={showKaizenForm}
          formProjectId={formProjectId}
          onKaizenSuccess={handleKaizenSuccess}
          onKaizenCancel={() => {
            setShowKaizenForm(false);
            setFormProjectId(null);
          }}
          onSelectedKaizenChange={setSelectedKaizen}
          measurementRequest={measurementRequest}
        />
      </div>
      {pickerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded bg-[var(--bg-surface)] p-4 shadow-lg">
            <h2 className="text-sm font-semibold text-[var(--text-primary)]">Selecione um projeto para criar o Kaizen.</h2>
            {projectsQuery.isLoading ? (
              <p className="mt-3 text-xs text-[var(--text-secondary)]">Carregando projetos...</p>
            ) : (projectsQuery.data ?? []).length === 0 ? (
              <p className="mt-3 text-xs text-[var(--text-secondary)]">Nenhum projeto disponível para vincular o Kaizen.</p>
            ) : (
              <select
                value={pickedProjectId}
                onChange={(event) => setPickedProjectId(event.target.value)}
                className="mt-3 w-full rounded border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 py-1 text-sm"
              >
                <option value="">Escolha o projeto</option>
                {(projectsQuery.data ?? []).map((project) => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
              </select>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setPickerOpen(false)} className="px-3 py-1 text-xs text-[var(--text-secondary)]">Cancelar</button>
              <button type="button" onClick={confirmProject} disabled={!pickedProjectId} className="rounded bg-[var(--brand-primary)] px-3 py-1 text-xs font-semibold text-white disabled:opacity-40">Continuar</button>
            </div>
          </div>
        </div>
      )}
    </div>
    </ModuleLayout>
  );
};
